import { ChevronRight } from 'lucide-react'
import PropTypes from 'prop-types'
import { NavLink } from 'react-router'

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/elements/collapsible'
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from '@/components/elements/sidebar'
import { EntitySelector } from '@/components/EntitySelector'
import { getNavItems } from '@/utils/routes'

/**
 * NavItems
 * Processes the routes and generates the navigation items (SidebarMenuItem).
 *
 * @param {object} props
 * @param {object} props.routes The routes to process
 * @param {string} props.activeEntity The active entity id
 * @param {array} props.roles The user's entity roles
 * @param {boolean} props.isXverseAdmin Whether the user is an Xverse admin
 * @param {boolean} [props.isXverseInstructorCandidate] Whether the user is an Xverse Instructor Candidate
 * @returns {*} The navigation items
 */
const NavItems = ({
  routes,
  activeEntity,
  activeOrganization,
  roles,
  isXverseAdmin,
  isXverseInstructorCandidate,
  entityAccess,
  entityAccessResolved,
  hasPublishedFaqs,
}) => {
  const { setOpen } = useSidebar()

  if (!routes?.children) return null

  const navItems = getNavItems(routes, {
    activeEntity,
    activeOrganization,
    roles,
    isXverseAdmin,
    isXverseInstructorCandidate,
    entityAccess,
    entityAccessResolved,
    hasPublishedFaqs,
  })

  return (
    <>
      {navItems.map((item) => {
        // Render collapsible group
        if (item.isGroup && item.items?.length > 0) {
          return (
            <Collapsible key={item.title} asChild className="group/collapsible" open>
              <SidebarMenuItem>
                {/* Group headers are static labels while the sidebar is
                    expanded (groups are forced open), but in the collapsed
                    icon rail they are the only visible affordance for the
                    group's links — so re-enable pointer events there and
                    expand the sidebar on click to reveal the sub-items. */}
                <CollapsibleTrigger
                  asChild
                  className="pointer-events-none group-data-[collapsible=icon]:pointer-events-auto group-data-[collapsible=icon]:cursor-pointer"
                >
                  <SidebarMenuButton tooltip={item.title} onClick={() => setOpen(true)}>
                    {item.icon && <item.icon />}
                    <span>{item.title}</span>
                    <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                  </SidebarMenuButton>
                </CollapsibleTrigger>
                <CollapsibleContent>
                  <SidebarMenuSub>
                    {item.items.map((subItem) => (
                      <SidebarMenuSubItem key={subItem.url}>
                        <NavLink to={subItem.url}>
                          {({ isActive }) => (
                            <SidebarMenuSubButton isActive={isActive} asChild>
                              <span>{subItem.title}</span>
                            </SidebarMenuSubButton>
                          )}
                        </NavLink>
                      </SidebarMenuSubItem>
                    ))}
                  </SidebarMenuSub>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          )
        }

        // Render simple menu item (non-group)
        return (
          <SidebarMenuItem key={item.url}>
            <NavLink to={item.url}>
              {({ isActive }) => (
                <SidebarMenuButton isActive={isActive} asChild>
                  <span>
                    {item.icon && <item.icon />}
                    <span>{item.title}</span>
                  </span>
                </SidebarMenuButton>
              )}
            </NavLink>
          </SidebarMenuItem>
        )
      })}
    </>
  )
}

export function Nav({
  allRoutes,
  activeEntity,
  activeOrganization,
  isXverseAdmin,
  isXverseInstructorCandidate,
  roles,
  entityAccess,
  entityAccessResolved,
  hasPublishedFaqs,
}) {
  const adminRoutes = allRoutes.find((r) => r.id === 'AdminRoutes')
  const entityRoutes = allRoutes.find((r) => r.id === 'EntityRoutes')
  const organizationRoutes = allRoutes.find((r) => r.id === 'OrganizationRoutes')

  const sharedProps = {
    activeEntity,
    activeOrganization,
    isXverseAdmin,
    isXverseInstructorCandidate,
    roles,
    entityAccess,
    entityAccessResolved,
    hasPublishedFaqs,
  }

  // A facility context takes precedence; otherwise an organization context;
  // otherwise the Xverse admin landing nav.
  return (
    <SidebarGroup>
      <SidebarMenu>
        <EntitySelector />
        {activeEntity && <NavItems routes={entityRoutes} {...sharedProps} />}
        {!activeEntity && activeOrganization && (
          <NavItems routes={organizationRoutes} {...sharedProps} />
        )}
        {!activeEntity && !activeOrganization && isXverseAdmin && (
          <NavItems routes={adminRoutes} {...sharedProps} />
        )}
      </SidebarMenu>
    </SidebarGroup>
  )
}

NavItems.propTypes = {
  routes: PropTypes.object,
  activeEntity: PropTypes.string,
  activeOrganization: PropTypes.string,
  roles: PropTypes.array,
  isXverseAdmin: PropTypes.bool,
  isXverseInstructorCandidate: PropTypes.bool,
  entityAccess: PropTypes.object,
  entityAccessResolved: PropTypes.bool,
  hasPublishedFaqs: PropTypes.bool,
}

Nav.propTypes = {
  allRoutes: PropTypes.array,
  activeEntity: PropTypes.string,
  activeOrganization: PropTypes.string,
  isXverseAdmin: PropTypes.bool,
  isXverseInstructorCandidate: PropTypes.bool,
  roles: PropTypes.array,
  entityAccess: PropTypes.object,
  entityAccessResolved: PropTypes.bool,
  hasPublishedFaqs: PropTypes.bool,
}
